import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Mail } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { scrollToSection } from "@/lib/scroll";

const LINES = [
  { text: "L'EXCELLENCE", gold: false },
  { text: "DU DETAILING", gold: true },
  { text: "AU QUÉBEC", gold: false },
];

const STATS = [
  { value: "500+", label: "Véhicules choyés" },
  { value: "5,0", label: "Note moyenne clients" },
  { value: "10 ans", label: "D'expertise en esthétique" },
  { value: "100 %", label: "Travail à la main" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="BMW noire brillante après un detailing complet"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[#050505]" style={{ maskImage: "linear-gradient(to top, black 30%, transparent)", WebkitMaskImage: "linear-gradient(to top, black 30%, transparent)" }} />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-40 pb-10">
        <motion.p
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-body text-xs uppercase tracking-[0.4em] text-gold mb-8 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-gold inline-block" />
          Drummondville · Centre-du-Québec
        </motion.p>

        <h1 data-testid="hero-title" className="font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl sm:text-5xl lg:text-6xl mb-8">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.gold ? "gold-gradient-text" : "text-white"}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.45 + i * 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-xl text-base md:text-lg text-white/70 font-light leading-relaxed mb-10"
        >
          Traitement antirouille, protection céramique, polissage et detailing complet.
          Chaque véhicule quitte notre atelier avec un fini digne d'une salle d'exposition.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-center gap-4 mb-16"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollToSection("#contact")}
            className="group relative rounded-full bg-gold px-8 py-4 font-display font-bold uppercase tracking-widest text-sm text-black overflow-hidden transition-transform duration-300 hover:-translate-y-1"
          >
            <span className="relative z-10">Obtenir ma soumission</span>
            <span className="absolute inset-0 bg-gold-light translate-y-full transition-transform duration-400 ease-out group-hover:translate-y-0" />
          </button>
          <a
            data-testid="hero-phone-link"
            href="tel:5142690261"
            className="flex items-center gap-3 rounded-full border border-white/20 bg-white/5 backdrop-blur-xl px-8 py-4 text-sm text-white transition-[border-color,background-color,transform] duration-300 hover:border-gold/60 hover:bg-gold/10 hover:-translate-y-1"
          >
            <Phone size={16} className="text-gold" />
            514-269-0261
          </a>
          <a
            data-testid="hero-email-link"
            href="mailto:premium.autoshine.mtl@gmail.com"
            className="flex items-center gap-3 rounded-full border border-white/20 bg-white/5 backdrop-blur-xl px-8 py-4 text-sm text-white transition-[border-color,background-color,transform] duration-300 hover:border-gold/60 hover:bg-gold/10 hover:-translate-y-1"
          >
            <Mail size={16} className="text-gold" />
            premium.autoshine.mtl@gmail.com
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-stats-panel"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="glass-gold rounded-2xl grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-white/10 overflow-hidden"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.slice(0, 8).replace(/\s/g, "-")}`} className="px-6 py-6">
              <p className="font-display font-black text-2xl sm:text-3xl text-gold mb-1">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-white/60">{s.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollToSection("#avant-apres")}
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="absolute bottom-8 right-8 hidden lg:flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-white/50 hover:text-gold hover:border-gold/50 transition-colors duration-300"
          aria-label="Défiler vers le bas"
        >
          <ArrowDown size={18} />
        </motion.button>
      </motion.div>
    </section>
  );
};
